import React, { useState, useEffect } from 'react';
import { useApp } from '../../context/AppContext.tsx';
import {
  Briefcase,
  ChevronLeft,
  ChevronRight,
  CheckCircle2,
  Images,
  Play,
  Film,
  FileText,
  Download,
  Eye,
  X,
  Share2,
  ArrowLeft,
  Check,
  Calendar,
  ExternalLink,
} from 'lucide-react';

interface PortfolioDetailPageProps {
  slugOrId: string;
}

export default function PortfolioDetailPage({ slugOrId }: PortfolioDetailPageProps) {
  const { portfolio, settings, navigate, copyPageUrl, showToast } = useApp();
  const [activeMediaIndex, setActiveMediaIndex] = useState<number>(0);
  const [previewPdfUrl, setPreviewPdfUrl] = useState<string | null>(null);
  const [copiedShare, setCopiedShare] = useState<boolean>(false);

  // Find portfolio item by slug or id
  const project =
    portfolio.find((p) => p.slug === slugOrId || p.id === slugOrId) ||
    portfolio.find((p) => p.slug === 'profil-project-utama') ||
    portfolio[0];

  useEffect(() => {
    if (project?.title) {
      document.title = `${project.title} | ${settings?.siteName || ''}`;
    }
  }, [project?.title, settings?.siteName]);

  if (!project) {
    return (
      <div className="min-h-[70vh] flex flex-col items-center justify-center p-6 text-center">
        <div className="p-8 rounded-3xl bg-slate-50 border border-slate-200 max-w-md mx-auto space-y-3">
          <Briefcase className="w-12 h-12 text-slate-400 mx-auto" />
          <h2 className="font-extrabold text-lg text-slate-900">Halaman Project Tidak Ditemukan</h2>
          <p className="text-xs text-slate-500">
            Profil proyek yang Anda cari belum tersedia atau telah dipindahkan.
          </p>
          <button
            type="button"
            onClick={() => navigate('home')}
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-emerald-700 text-white font-bold text-xs hover:bg-emerald-800 transition-colors cursor-pointer"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Kembali ke Beranda</span>
          </button>
        </div>
      </div>
    );
  }

  // Media Helpers
  const getYouTubeId = (url?: string) => {
    if (!url) return null;
    const match = url.match(/(?:youtu\.be\/|youtube\.com\/(?:embed\/|v\/|watch\?v=|watch\?.+&v=))([\w-]{11})/);
    return match ? match[1] : null;
  };

  const extractIframeSrc = (code?: string) => {
    if (!code) return '';
    const match = code.match(/src=["']([^"']+)["']/i);
    return match ? match[1] : '';
  };

  const isDirectVideo = (url?: string) =>
    Boolean(url) && (/\.(mp4|webm|mov|mkv)(\?.*)?$/i.test(url) || url.includes('/uploads/'));

  interface MediaItem {
    type: 'photo' | 'video';
    url: string;
    videoEmbed?: string;
  }

  const mediaItems: MediaItem[] = [];
  if (project.videoUrl || project.videoEmbed) {
    mediaItems.push({
      type: 'video',
      url: project.videoUrl || '',
      videoEmbed: project.videoEmbed,
    });
  }
  const photos = [project.coverImage, ...(project.gallery || [])].filter(Boolean);
  photos.forEach((img) => {
    if (img !== project.videoUrl) {
      mediaItems.push({ type: 'photo', url: img });
    }
  });

  const currentMedia = mediaItems[activeMediaIndex] || mediaItems[0];

  const handleNextMedia = () => {
    if (mediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === mediaItems.length - 1 ? 0 : prev + 1));
  };

  const handlePrevMedia = () => {
    if (mediaItems.length <= 1) return;
    setActiveMediaIndex((prev) => (prev === 0 ? mediaItems.length - 1 : prev - 1));
  };

  const handleShare = async () => {
    const success = await copyPageUrl(window.location.href, 'Tautan Project');
    if (success) {
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 3000);
    }
  };

  return (
    <div className="py-8 sm:py-12 bg-slate-50/50 min-h-screen">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-8">
        
        {/* Breadcrumb & Navigation Header */}
        <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200/80 pb-4">
          <nav className="flex items-center gap-2 text-xs font-semibold text-slate-500 overflow-x-auto">
            <button
              type="button"
              onClick={() => navigate('home')}
              className="hover:text-emerald-700 transition-colors cursor-pointer shrink-0"
            >
              Beranda
            </button>
            <span>/</span>
            <span className="text-slate-900 font-bold truncate max-w-[200px] sm:max-w-xs">
              {project.title}
            </span>
          </nav>

          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-white border border-slate-200 text-slate-700 hover:text-emerald-700 hover:border-emerald-300 font-bold text-xs shadow-2xs transition-all cursor-pointer"
            >
              {copiedShare ? <Check className="w-4 h-4 text-emerald-600" /> : <Share2 className="w-4 h-4" />}
              <span>{copiedShare ? 'Tersalin!' : 'Bagikan'}</span>
            </button>

            <button
              type="button"
              onClick={() => navigate('home')}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs shadow-2xs transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Kembali</span>
            </button>
          </div>
        </div>

        {/* Title & Summary Area */}
        <div className="max-w-4xl space-y-3">
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight leading-tight">
            {project.title}
          </h1>

          {project.summary && (
            <p className="text-sm sm:text-base text-slate-600 leading-relaxed font-medium">
              {project.summary}
            </p>
          )}
        </div>

        {/* Media Gallery Showcase Area */}
        <div className="space-y-3">
          <div className="relative aspect-16/10 sm:aspect-16/9 rounded-2xl sm:rounded-3xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200">
            {currentMedia?.type === 'video' ? (
              isDirectVideo(currentMedia.url) ? (
                <video
                  src={currentMedia.url}
                  controls
                  playsInline
                  className="w-full h-full object-contain"
                />
              ) : currentMedia.videoEmbed && currentMedia.videoEmbed.includes('<iframe') ? (
                <iframe
                  src={extractIframeSrc(currentMedia.videoEmbed) || currentMedia.url}
                  title={project.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : getYouTubeId(currentMedia.url) ? (
                <iframe
                  src={`https://www.youtube.com/embed/${getYouTubeId(currentMedia.url)}`}
                  title={project.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              ) : (
                <iframe
                  src={currentMedia.url}
                  title={project.title}
                  className="w-full h-full border-0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              )
            ) : (
              <img
                src={
                  currentMedia?.url ||
                  project.coverImage ||
                  'https://images.unsplash.com/photo-1511578314322-379afb476865?auto=format&fit=crop&w=1200&q=80'
                }
                alt={project.title}
                className="w-full h-full object-cover"
              />
            )}

            {/* Slide Arrows */}
            {mediaItems.length > 1 && (
              <>
                <button
                  type="button"
                  onClick={handlePrevMedia}
                  className="absolute left-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/65 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all cursor-pointer z-10 border border-white/20 active:scale-95"
                  aria-label="Media Sebelumnya"
                >
                  <ChevronLeft className="w-6 h-6" />
                </button>
                <button
                  type="button"
                  onClick={handleNextMedia}
                  className="absolute right-3 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-black/65 hover:bg-black/85 text-white flex items-center justify-center shadow-lg transition-all cursor-pointer z-10 border border-white/20 active:scale-95"
                  aria-label="Media Selanjutnya"
                >
                  <ChevronRight className="w-6 h-6" />
                </button>
              </>
            )}

            {/* Counter Badge */}
            {mediaItems.length > 1 && (
              <div className="absolute bottom-3 right-3 bg-black/80 backdrop-blur-xs text-white text-xs font-bold px-3.5 py-1.5 rounded-xl flex items-center gap-2 border border-white/20 shadow-md">
                {currentMedia?.type === 'video' ? (
                  <Film className="w-4 h-4 text-purple-400" />
                ) : (
                  <Images className="w-4 h-4 text-emerald-400" />
                )}
                <span>
                  {activeMediaIndex + 1} / {mediaItems.length} Media
                </span>
              </div>
            )}
          </div>

          {/* Thumbnail Strip */}
          {mediaItems.length > 1 && (
            <div className="flex items-center gap-3 overflow-x-auto pb-2 scrollbar-thin">
              {mediaItems.map((item, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => setActiveMediaIndex(idx)}
                  className={`relative w-24 h-16 rounded-xl overflow-hidden border-2 transition-all shrink-0 cursor-pointer bg-slate-900 ${
                    activeMediaIndex === idx
                      ? 'border-emerald-600 scale-105 shadow-md ring-2 ring-emerald-500/30'
                      : 'border-transparent opacity-60 hover:opacity-100'
                  }`}
                >
                  {item.type === 'video' ? (
                    <div className="w-full h-full flex items-center justify-center bg-purple-950/90 text-white">
                      <Play className="w-5 h-5 fill-current text-purple-300" />
                    </div>
                  ) : (
                    <img src={item.url} alt={`Thumbnail ${idx + 1}`} className="w-full h-full object-cover" />
                  )}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Detailed Narrative & PDF Files Section */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          
          {/* Main Story Narrative */}
          <div className="lg:col-span-8 space-y-6 bg-white p-6 sm:p-8 rounded-3xl border border-slate-200/90 shadow-2xs">
            {project.description ? (
              <div className="prose max-w-none text-slate-700 text-sm sm:text-base leading-relaxed whitespace-pre-line space-y-4">
                {project.description}
              </div>
            ) : (
              <p className="text-slate-500 text-sm italic">
                Naskah cerita lengkap project belum ditambahkan.
              </p>
            )}

            {/* Highlights if present */}
            {project.highlights && project.highlights.length > 0 && (
              <div className="pt-4 border-t border-slate-100 space-y-3">
                <h3 className="text-xs font-extrabold text-slate-500 uppercase tracking-wider">
                  Poin Capaian & Hasil Utama Project:
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                  {project.highlights.map((h, idx) => (
                    <div
                      key={idx}
                      className="flex items-start gap-2.5 p-3 rounded-2xl bg-emerald-50/60 border border-emerald-100 text-slate-800 text-xs font-semibold"
                    >
                      <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
                      <span>{h}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sidebar / Attached PDF Documents Section */}
          <div className="lg:col-span-4 space-y-6">
            
            {/* PDF FILES CARD */}
            <div className="bg-white p-6 rounded-3xl border border-slate-200/90 shadow-2xs space-y-4">
              <div className="flex items-center gap-2.5 pb-3 border-b border-slate-100">
                <div className="p-2.5 rounded-2xl bg-rose-50 text-rose-600 border border-rose-100">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <h3 className="font-extrabold text-slate-900 text-sm sm:text-base">
                    Dokumen PDF & Berkas Project
                  </h3>
                  <p className="text-[11px] text-slate-500">
                    {project.pdfFiles && project.pdfFiles.length > 0
                      ? `${project.pdfFiles.length} berkas PDF tersedia`
                      : 'Berkas lampiran laporan PDF'}
                  </p>
                </div>
              </div>

              {/* List of Attached PDF Files */}
              {project.pdfFiles && project.pdfFiles.length > 0 ? (
                <div className="space-y-3">
                  {project.pdfFiles.map((pdf, idx) => (
                    <div
                      key={pdf.id || idx}
                      className="p-3.5 rounded-2xl bg-slate-50 hover:bg-slate-100/80 border border-slate-200 transition-all space-y-2.5 group"
                    >
                      <div className="flex items-start gap-2.5">
                        <FileText className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
                        <div className="flex-1 min-w-0">
                          <h4 className="font-extrabold text-slate-900 text-xs sm:text-sm truncate group-hover:text-emerald-700 transition-colors">
                            {pdf.title || `Dokumen Project #${idx + 1}.pdf`}
                          </h4>
                          {pdf.size && (
                            <span className="text-[10px] font-bold text-slate-400 block mt-0.5">
                              Ukuran File: {pdf.size}
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Action buttons for PDF */}
                      <div className="flex items-center gap-2 pt-1 border-t border-slate-200/60">
                        <button
                          type="button"
                          onClick={() => setPreviewPdfUrl(pdf.url)}
                          className="flex-1 py-1.5 px-3 rounded-xl bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer border border-emerald-200/60"
                        >
                          <Eye className="w-3.5 h-3.5 text-emerald-600" />
                          <span>Lihat PDF</span>
                        </button>

                        <a
                          href={pdf.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          download
                          className="py-1.5 px-3 rounded-xl bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs flex items-center justify-center gap-1.5 transition-colors cursor-pointer"
                          title="Buka atau Unduh Berkas PDF"
                        >
                          <Download className="w-3.5 h-3.5 text-slate-300" />
                          <span>Buka / Unduh</span>
                        </a>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="p-4 rounded-2xl bg-slate-50 border border-dashed border-slate-200 text-center space-y-1">
                  <FileText className="w-8 h-8 text-slate-300 mx-auto" />
                  <p className="text-xs font-semibold text-slate-600">Belum ada berkas PDF diunggah.</p>
                  <p className="text-[10px] text-slate-400">
                    File PDF laporan dapat diunggah lewat Dashboard Admin di menu Portofolio Project.
                  </p>
                </div>
              )}
            </div>

            {/* Quick Share Box */}
            <div className="bg-gradient-to-br from-emerald-900 to-slate-900 text-white p-6 rounded-3xl space-y-3 shadow-md">
              <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-950/80 px-2.5 py-1 rounded-lg border border-emerald-500/30">
                Informasi & Bagikan Project
              </span>
              <h3 className="font-extrabold text-base text-white">
                Bagikan Profil Project Ini
              </h3>
              <p className="text-xs text-slate-300 leading-relaxed">
                Gunakan tautan ini untuk membagikan laporan dan dokumentasi kegiatan proyek kepada rekan bisnis atau klien.
              </p>
              <button
                type="button"
                onClick={handleShare}
                className="w-full py-2.5 rounded-xl bg-emerald-500 hover:bg-emerald-400 active:scale-98 text-slate-950 font-black text-xs shadow-md transition-all cursor-pointer flex items-center justify-center gap-2"
              >
                {copiedShare ? <Check className="w-4 h-4 text-slate-950" /> : <Share2 className="w-4 h-4" />}
                <span>{copiedShare ? 'Tautan Berhasil Disalin!' : 'Salin Tautan Project'}</span>
              </button>
            </div>

          </div>
        </div>

      </div>

      {/* Embedded PDF Viewer Modal */}
      {previewPdfUrl && (
        <div className="fixed inset-0 z-50 bg-slate-950/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200">
          <div className="bg-white rounded-3xl shadow-2xl border border-slate-200 w-full max-w-5xl h-[88vh] flex flex-col overflow-hidden animate-in zoom-in-95 duration-200">
            
            {/* Modal Header */}
            <div className="bg-slate-900 text-white p-4 sm:p-5 flex items-center justify-between border-b border-slate-800">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-rose-500/20 text-rose-400 border border-rose-500/30">
                  <FileText className="w-5 h-5" />
                </div>
                <div>
                  <span className="text-[10px] font-black uppercase text-emerald-400 block">
                    Pratinjau Dokumen PDF
                  </span>
                  <h3 className="font-extrabold text-sm sm:text-base text-white truncate max-w-xs sm:max-w-md">
                    {project.title} - File PDF
                  </h3>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <a
                  href={previewPdfUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  download
                  className="px-3 py-1.5 rounded-xl bg-emerald-600 hover:bg-emerald-500 text-white font-bold text-xs flex items-center gap-1.5 transition-colors"
                >
                  <Download className="w-3.5 h-3.5" />
                  <span className="hidden sm:inline">Unduh PDF</span>
                </a>
                <button
                  type="button"
                  onClick={() => setPreviewPdfUrl(null)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal PDF Iframe Body */}
            <div className="flex-1 bg-slate-100 relative">
              <iframe
                src={previewPdfUrl}
                title="Pratinjau PDF"
                className="w-full h-full border-0"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
